import styled from 'styled-components';
import { WorkspaceOptionsState, workspaceOptions$ } from './workspaceOptions$';
import { WorkspaceModelBrowser } from 'components/WorkspaceModelBrowser/WorkspaceModelBrowser';
import {
  createTemplate,
  exportWorkspaceToExcel,
  restoreDeleted,
} from './actions';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import {
  confirmDeleteWorkspace,
  openWorkspace,
} from 'streams/workspaces/actions';
import { toggleModelPreview } from 'streams/currentUser/actions';
import { RigidMarker } from './atoms';
import {
  SidebarMenu,
  SidebarMenuMenu,
  SidebarMenuSection,
} from '@ardoq/sidebar-menu';
import { isTruthy } from '@ardoq/common-helpers';
import { s24 } from '@ardoq/design-tokens';
import { CalculatedFieldSection } from './CalculatedFieldSection';
import { trackEvent, trackEventV2 } from 'tracking/tracking';
import { navigate } from '../../../router/declarative/streamRouterAdapter/actions';
import { FlexBox } from '@ardoq/layout';
import { t, type WithI18nLocale } from '@ardoq/i18n';

const ModelPreviewContainer = styled.div`
  padding: ${s24};
`;

const RigidLabel = ({ i18nLocale }: WithI18nLocale) => (
  <RigidMarker>
    {t(i18nLocale, 'menus:workspaceOptions.rigid', 'Rigid ')}
  </RigidMarker>
);

type WorkspaceOptionsProps = WorkspaceOptionsState & {
  onCloseClick?: VoidFunction;
  headerTitle?: string;
  closeButtonLabel?: string;
  closeButtonAriaLabel?: string;
};

const WorkspaceOptionsBase = ({
  contextWorkspace,
  hideModelPreview,
  showCreateTemplate,
  showEditModel,
  isFlexibleModel,
  isWsWriter,
  isWsAdmin,
  disableEditWorkspace,
  disableExportExcel,
  disableEditModel,
  disableWorkspaceEditReferenceTypes,
  disableWorkspaceEditFields,
  disableModelCreateTemplate,
  hasRelatedWorkspaces,
  relatedWorkspaces,
  calculatedFields,
  calculatedFieldEvents,
  componentTypeHierarchy,
  branchId,
  showCalculatedFieldSection,
  onCloseClick,
  headerTitle,
  closeButtonLabel,
  closeButtonAriaLabel,
  locale,
  i18nLocale,
}: WorkspaceOptionsProps) => {
  const editWorkspaceTitle = isWsAdmin
    ? t(
        i18nLocale,
        'menus:workspaceOptions.section.editWorkspace',
        'Edit workspace'
      )
    : t(i18nLocale, 'menus:workspaceOptions.section.workspace', 'Workspace');

  return contextWorkspace ? (
    <FlexBox width="full">
      <SidebarMenu
        onCloseClick={onCloseClick}
        headerTitle={headerTitle}
        closeButtonLabel={closeButtonLabel}
        closeButtonAriaLabel={closeButtonAriaLabel}
      >
        <SidebarMenuSection
          title={t(
            i18nLocale,
            'menus:workspaceOptions.section.metamodelPreview',
            'Metamodel preview'
          )}
          aria-label={t(
            i18nLocale,
            'menus:workspaceOptions.section.metamodelPreview',
            'Metamodel preview'
          )}
          isCollapsed={hideModelPreview}
          onClick={() => dispatchAction(toggleModelPreview())}
        >
          <ModelPreviewContainer>
            {!isFlexibleModel && <RigidLabel i18nLocale={i18nLocale} />}
            <WorkspaceModelBrowser root={componentTypeHierarchy} />
          </ModelPreviewContainer>
        </SidebarMenuSection>
        <SidebarMenuSection
          title={editWorkspaceTitle}
          aria-label={editWorkspaceTitle}
          isCollapsible={false}
        >
          <SidebarMenuMenu
            options={[
              ...(isWsWriter
                ? [
                    isWsAdmin && {
                      label: t(
                        i18nLocale,
                        'menus:workspaceOptions.editWorkspaceProperties',
                        'Edit workspace properties'
                      ),
                      onClick: () =>
                        dispatchAction(
                          navigate({
                            route: '/workspace-manager/:workspaceId/settings',
                            params: { workspaceId: contextWorkspace._id },
                          })
                        ),
                      isDisabled: disableEditWorkspace,
                      dataClickId: 'edit-workspace-properties-menu-item',
                    },
                    {
                      label: t(
                        i18nLocale,
                        'menus:workspaceOptions.viewWorkspaceHistory',
                        'View workspace history'
                      ),
                      onClick: () => {
                        trackEvent('Clicked', {
                          id: 'view-workspace-history-menu-item',
                        });
                        dispatchAction(
                          navigate({
                            route: '/workspace-manager/:workspaceId/history',
                            params: { workspaceId: contextWorkspace._id },
                          })
                        );
                      },

                      isDisabled: disableEditWorkspace,
                      dataTestId: 'view-workspace-history-menu-item',
                    },
                    {
                      label: t(
                        i18nLocale,
                        'menus:workspaceOptions.restoreDeleted',
                        'Restore deleted components and references'
                      ),
                      onClick: () =>
                        dispatchAction(
                          restoreDeleted({
                            id: contextWorkspace._id,
                            workspaceName: contextWorkspace.name,
                          })
                        ),
                      isDisabled: disableEditWorkspace,
                      dataClickId:
                        'restore-deleted-components-and-references-menu-item',
                    },
                    isWsAdmin && {
                      label: t(
                        i18nLocale,
                        'menus:workspaceOptions.deleteWorkspace',
                        'Delete workspace'
                      ),
                      onClick: () => {
                        if (contextWorkspace) {
                          dispatchAction(
                            confirmDeleteWorkspace(contextWorkspace)
                          );
                        }
                      },
                      isDisabled: disableEditWorkspace,
                      dataClickId: 'delete-workspace-menu-item',
                    },
                  ]
                : []),
              {
                label: t(
                  i18nLocale,
                  'menus:workspaceOptions.exportWorkspaceToExcel',
                  'Export workspace to excel'
                ),
                onClick: () =>
                  dispatchAction(
                    exportWorkspaceToExcel({
                      workspaceId: contextWorkspace._id,
                      filename: contextWorkspace.name,
                    })
                  ),
                isDisabled: disableExportExcel,
                dataClickId: 'export-workspace-to-excel-menu-item',
              },
            ].filter(isTruthy)}
          />
        </SidebarMenuSection>

        {showEditModel && (
          <SidebarMenuSection
            title={t(
              i18nLocale,
              'menus:workspaceOptions.section.editMetamodel',
              'Edit metamodel'
            )}
            aria-label={t(
              i18nLocale,
              'menus:workspaceOptions.section.editMetamodel',
              'Edit metamodel'
            )}
            isCollapsible={false}
          >
            <SidebarMenuMenu
              options={[
                {
                  label: t(
                    i18nLocale,
                    'menus:workspaceOptions.manageComponentTypes',
                    'Manage component types'
                  ),
                  onClick: () => {
                    trackEvent(
                      'Workspace menu0 clicked manage component types'
                    );
                    dispatchAction(
                      navigate({
                        route:
                          '/workspace-manager/:workspaceId/metamodel/component-types',
                        params: { workspaceId: contextWorkspace._id },
                      })
                    );
                  },
                  isDisabled: disableEditModel,
                  dataClickId: 'manage-component-types-and-metamodel-menu-item',
                },
                {
                  label: t(
                    i18nLocale,
                    'menus:workspaceOptions.manageReferenceTypes',
                    'Manage reference types'
                  ),
                  onClick: () => {
                    trackEvent(
                      'Workspace menu0 clicked manage reference types'
                    );
                    dispatchAction(
                      navigate({
                        route:
                          '/workspace-manager/:workspaceId/metamodel/reference-types',
                        params: { workspaceId: contextWorkspace._id },
                      })
                    );
                  },
                  isDisabled: disableWorkspaceEditReferenceTypes,
                  dataClickId: 'manage-reference-types-menu-item',
                },
                {
                  label: t(
                    i18nLocale,
                    'menus:workspaceOptions.manageFields',
                    'Manage fields'
                  ),
                  onClick: () => {
                    trackEventV2({
                      event: 'Workspace menu0 clicked manage fields',
                    });
                    dispatchAction(
                      navigate({
                        route:
                          '/workspace-manager/:workspaceId/metamodel/fields',
                        params: { workspaceId: contextWorkspace._id },
                      })
                    );
                  },
                  isDisabled: disableWorkspaceEditFields,
                  dataClickId: 'manage-field-types-menu-item',
                },
                showCreateTemplate && {
                  label: t(
                    i18nLocale,
                    'menus:workspaceOptions.createTemplate',
                    'Create template from workspace metamodel'
                  ),
                  onClick: () =>
                    dispatchAction(createTemplate(contextWorkspace._id)),
                  isDisabled: disableModelCreateTemplate,
                  dataClickId:
                    'create-template-from-workspace-metamodel-menu-item',
                },
              ].filter(isTruthy)}
            />
          </SidebarMenuSection>
        )}

        {hasRelatedWorkspaces && (
          <SidebarMenuSection
            title={t(
              i18nLocale,
              'menus:workspaceOptions.section.relatedWorkspaces',
              'Related workspaces'
            )}
            aria-label={t(
              i18nLocale,
              'menus:workspaceOptions.section.relatedWorkspaces',
              'Related workspaces'
            )}
            isCollapsible={false}
          >
            <SidebarMenuMenu
              options={relatedWorkspaces.map(({ id, name: label }) => {
                return {
                  label,
                  onClick: () =>
                    dispatchAction(
                      openWorkspace({
                        workspaceId: id,
                        keepCurrentContext: true,
                        trackingLocation: 'optionsMenuSidebar',
                      })
                    ),
                  dataClickId: `related-workspace-${id}-menu-item`,
                };
              })}
            />
          </SidebarMenuSection>
        )}

        {showCalculatedFieldSection && (
          <CalculatedFieldSection
            calculatedFields={calculatedFields}
            fieldStatusMap={calculatedFieldEvents}
            branchId={branchId}
            locale={locale}
            i18nLocale={i18nLocale}
          />
        )}
      </SidebarMenu>
    </FlexBox>
  ) : (
    <div />
  );
};
export const WorkspaceOptions = connect(
  WorkspaceOptionsBase,
  workspaceOptions$
);
